import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Tabs,
  Typography,
  TextField,
  InputAdornment,
} from '@mui/material';
import CampaignIcon from '@mui/icons-material/Campaign';
import SearchIcon from '@mui/icons-material/Search';
import SmsIcon from '@mui/icons-material/Sms';
import StarIcon from '@mui/icons-material/Star';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Button, IconButton } from '@mui/material';

import React, { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { BhiDialog, FavoriteStarIcon } from 'src/UIKit';
import { HiddenScrollbarArea } from './HiddenScrollbarArea.styles';
import { LargeTab } from './LargeTab.styles';
import { RaisedIconButton } from './RaisedIconButton.styles';
import { CampaignTypeSelectionDialogController } from '../../controllers';
import { getActiveConfigCategories } from 'src/modules/campaignConfigBuilder/slices';

import { CampaignConfigView } from 'src/modules/campaignConfigBuilder/types';
import { SafeIcon } from 'src/components/SafeIcon/SafeIcon';
import { AuthorLabel } from 'src/components/AuthorLabel';
import toast from 'react-hot-toast';
import { canManagePresets } from 'src/modules/campaignPresets/helpers/canManagePresets';
import { PresetCard } from 'src/modules/campaignPresets/components/PresetCard/PresetCard';
import { EditPresetDialog } from 'src/modules/campaignPresets/components/EditPresetDialog/EditPresetDialog';
import { useSetCampaignPresetStatusApi } from 'src/api/campaignPresets';
import { CampaignPreset } from 'src/api/campaignPresets/types';
import { CampaignCreateOption } from 'src/modules/campaignCreateOptions/api';
import {
  getCampaignCreateOptions,
  getCampaignCreateOptionsLoading,
  getCampaignCreateOptionsError,
  removeCreateOption,
} from 'src/modules/campaignCreateOptions/slices';
import {
  useToggleCampaignCreateOptionFavorite,
  useCampaignCreateOptionsLoader,
} from 'src/modules/campaignCreateOptions/hooks';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { selectProfile } from 'src/modules/profile/slices';
import useConfirmationDialog from 'src/components/ConfirmationDialog/view';
import { useDispatch } from 'react-redux';
import { campaignBuilderPath } from '../../helpers';

// Synthetic "Favorites" tab identifier, kept distinct from category names.
const FAVORITES_TAB_VALUE = '__favorites__';

// Campaign type to icon name mapping
const campaignTypeIconMap: Record<string, string> = {
  'Annual Birthday Message': 'Celebration',
  'Calendar Event Reminder': 'Alarm',
  'Contact-to-Group': 'GroupAdd',
  'Poll': 'Poll',
  'Survey': 'Poll',
  'Text Flyer': 'Mms',
  'Text-to-Join': 'PersonAdd',
  'Custom Annual Message': 'CalendarMonth',
  'Custom Bulk Message': 'Sms',
  'Custom Join Message': 'Add',
  'Recurring Campaign': 'EventRepeat',
};

const getCategoryIcon = (icon: string) => {
  if (!icon) return <CampaignIcon fontSize="small" />;
  if (icon.startsWith('data:image/') || icon.startsWith('http')) {
    return <img src={icon} alt="" style={{ width: 20, height: 20, objectFit: 'contain' }} />;
  }
  return <SafeIcon name={icon} fontSize="small" fallback={<CampaignIcon fontSize="small" />} />;
};

const configIconSx = { width: 22, height: 22 };

// scope rather than re-created every render and re-run per card.
const getConfigIcon = (name: string, icon?: string) => {
  if (!icon) {
    const iconName = campaignTypeIconMap[name];
    if (iconName) {
      return <SafeIcon name={iconName} fontSize="small" sx={configIconSx} fallback={<SmsIcon sx={configIconSx} />} />;
    }
    return <SmsIcon sx={configIconSx} />;
  }
  if (icon.startsWith('data:image/') || icon.startsWith('http')) {
    return <img src={icon} alt="" style={{ width: 22, height: 22, objectFit: 'contain' }} />;
  }
  return <SafeIcon name={icon} fontSize="small" sx={configIconSx} fallback={<SmsIcon sx={configIconSx} />} />;
};

interface CampaignTypeSelectionDialogProps extends CampaignTypeSelectionDialogController {}

export const CampaignTypeSelectionDialog: FC<CampaignTypeSelectionDialogProps> = props => {
  const { isOpen, close } = props;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const activeCategories = useSelector(getActiveConfigCategories);
  const createOptions = useSelector(getCampaignCreateOptions);
  const loading = useSelector(getCampaignCreateOptionsLoading);
  const error = useSelector(getCampaignCreateOptionsError);
  const { toggleFavorite, pendingIds } = useToggleCampaignCreateOptionFavorite();
  const { loadCreateOptions } = useCampaignCreateOptionsLoader();

  const showError = !!error && createOptions.length === 0;

  const { profile } = useSelector(selectProfile);
  const companyId = useSelector(getCurrentCompanyId);
  // Creating, editing and deleting presets are all restricted to the same set:
  // managers and above (or admins).
  const canArchive = canManagePresets(profile);
  const canEditPresets = canArchive;

  const setStatus = useSetCampaignPresetStatusApi();
  const { openDialog, ConfirmationDialog } = useConfirmationDialog();

  const [activeTab, setActiveTab] = React.useState<string | null>(null);
  const [editingPreset, setEditingPreset] = React.useState<CampaignPreset | null>(null);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [canScrollUp, setCanScrollUp] = React.useState(false);
  const [canScrollDown, setCanScrollDown] = React.useState(false);
  const [showScrollArrows, setShowScrollArrows] = React.useState(false);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  // Clear state when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setActiveTab(null);
      setSearchQuery('');
      setCanScrollUp(false);
      setCanScrollDown(false);
      setShowScrollArrows(false);
    }
  }, [isOpen]);

  // Check scroll position and update arrow states
  const checkScrollPosition = React.useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const isScrollable = scrollHeight > clientHeight;

    setShowScrollArrows(isScrollable);
    setCanScrollUp(scrollTop > 0);
    setCanScrollDown(scrollTop < scrollHeight - clientHeight - 1); // -1 for rounding errors
  }, []);


  // Scroll functions
  const scrollUp = () => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollBy({ top: -200, behavior: 'smooth' });
    }
  };

  const scrollDown = () => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollBy({ top: 200, behavior: 'smooth' });
    }
  };


  const optionsByCategory = React.useMemo(() => {
    return createOptions.reduce<Record<string, CampaignCreateOption[]>>((acc, option) => {
      if (!acc[option.category]) {
        acc[option.category] = [];
      }
      acc[option.category].push(option);
      return acc;
    }, {});
  }, [createOptions]);

  const favoriteOptions = React.useMemo(() => {
    return createOptions.filter(option => option.favorite);
  }, [createOptions]);

  // Config views indexed by config id, used to resolve a preset's source-config
  // icon. Key on the config's own id (not the opaque option id) so a preset's
  // sourceConfigId resolves correctly.
  const configsById = React.useMemo(() => {
    return createOptions.reduce<Record<string, CampaignConfigView>>((acc, option) => {
      if (option.kind === 'config' && option.config) {
        acc[option.config.id] = option.config;
      }
      return acc;
    }, {});
  }, [createOptions]);

  const visibleCategories = React.useMemo(() => {
    return activeCategories.filter(category => optionsByCategory[category.name]?.length > 0);
  }, [activeCategories, optionsByCategory]);

  // The synthetic Favorites tab always leads the strip: whenever any category
  // tab exists (even with zero favorites, in which case its content area shows
  // instructions) AND whenever an option is favorited but its category isn't
  // active (the Favorites tab then stands alone). Only when there are neither
  // category tabs nor favorites do we render none, so the "No campaign
  // categories available." message shows instead.
  const visibleTabs = React.useMemo(() => {
    const categoryTabs = visibleCategories.map(category => ({
      name: category.name,
      title: category.title,
      icon: getCategoryIcon(category.icon),
      options: optionsByCategory[category.name] || [],
    }));

    if (categoryTabs.length === 0 && favoriteOptions.length === 0) {
      return [];
    }

    return [
      {
        name: FAVORITES_TAB_VALUE,
        title: 'Favorites',
        icon: <StarIcon fontSize="small" />,
        options: favoriteOptions,
      },
      ...categoryTabs,
    ];
  }, [optionsByCategory, favoriteOptions, visibleCategories]);

  // Default to the first category tab (right of Favorites) so users land on the
  // template list and discover new ones. When the strip is Favorites-only (a
  // favorited option whose category isn't active), fall back to the Favorites
  // tab so it's selected rather than leaving the content area blank.
  const defaultTabValue =
    visibleTabs.find(tab => tab.name !== FAVORITES_TAB_VALUE)?.name ??
    visibleTabs[0]?.name ??
    null;

  const handleTabChange = (_: React.SyntheticEvent, value: string) => {
    setActiveTab(value);
  };

  const visibleOptions = React.useMemo(() => {
    if (!activeTab) return [];

    const activeOptions = visibleTabs.find(tab => tab.name === activeTab)?.options || [];
    if (!searchQuery.trim()) return activeOptions;

    const query = searchQuery.toLowerCase();
    return activeOptions.filter(option => {
      if (option.kind === 'config' && option.config) {
        return (
          option.config.config.name.toLowerCase().includes(query) ||
          option.config.config.description.toLowerCase().includes(query)
        );
      }
      if (option.kind === 'preset' && option.preset) {
        return (
          option.preset.name.toLowerCase().includes(query) ||
          option.preset.description.toLowerCase().includes(query) ||
          option.preset.type.toLowerCase().includes(query)
        );
      }
      return false;
    });
  }, [activeTab, searchQuery, visibleTabs]);

  // The Favorites tab with zero favorites shows an instructional empty state
  // instead of a list.
  const isFavoritesEmpty = activeTab === FAVORITES_TAB_VALUE && favoriteOptions.length === 0;

  React.useEffect(() => {
    // Recompute when the tab or the number of visible items changes — that is
    // what affects scrollability. Depending on the visibleOptions array identity
    // would reschedule this timeout on every keystroke (the filter memo returns a
    // fresh array each time) even when the count is unchanged.
    const timeoutId = setTimeout(checkScrollPosition, 100);
    return () => clearTimeout(timeoutId);
  }, [activeTab, visibleOptions.length, checkScrollPosition]);

  React.useEffect(() => {
    if (!isOpen || loading) {
      return;
    }

    // No tabs at all (no categories).
    if (defaultTabValue === null) {
      if (activeTab !== null) {
        setActiveTab(null);
      }
      return;
    }

    const activeTabValid = visibleTabs.some(tab => tab.name === activeTab);

    // Open the default tab (first category), or re-select it if the current tab
    // is no longer available.
    if (activeTab === null || !activeTabValid) {
      setActiveTab(defaultTabValue);
    }
  }, [isOpen, loading, activeTab, visibleTabs, defaultTabValue]);


  const renderTab = (tab: {
    name: string;
    title: string;
    icon: React.ReactNode;
    options: CampaignCreateOption[];
  }) => {
    const isFavorites = tab.name === FAVORITES_TAB_VALUE;
    return (
      <LargeTab
        key={tab.name}
        value={tab.name}
        aria-label={isFavorites ? 'Favorites' : undefined}
        iconOnly={isFavorites}
        label={
          <Box display="flex" alignItems="center" gap={1}>
            {tab.icon}
            {!isFavorites && <span>{tab.title}</span>}
          </Box>
        }
      />
    );
  };

  const handleOptionClick = (option: CampaignCreateOption) => {
    if (option.kind === 'config' && option.config) {
      navigate(campaignBuilderPath(`?configId=${option.config.id}`));
      close();
      return;
    }

    if (option.kind === 'preset' && option.preset) {
      navigate(campaignBuilderPath(`?presetId=${option.preset.id}`));
      close();
    }
  };

  const handleArchivePreset = async (option: CampaignCreateOption) => {
    if (!companyId || !option.preset) return;

    const name = option.preset.name;
    const presetId = option.preset.id;

    openDialog({
      title: `Delete "${name}"`,
      content: `Existing campaigns are unaffected. Delete?`,
      confirmButtonText: 'Delete',
      onConfirm: async () => {
        try {
          await setStatus(presetId, 'archived', companyId);
          dispatch(removeCreateOption({ kind: 'preset', id: option.id }));
        } catch (e) {
          console.error('Failed to archive campaign preset:', e);
          toast.error('Failed to delete preset. Please try again.');
        }
      },
    });
  };

  const handleRetry = () => {
    if (companyId) {
      loadCreateOptions(true, companyId);
    }
  };

  return (
    <BhiDialog isOpen={isOpen} onClose={close} title="Choose Campaign Type" maxWidth="md">
      <Box
        sx={{
          padding: '29px',
          width: '740px',
          minHeight: '400px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ mb: 2 }}>
            <CircularProgress />
          </Box>
        ) : showError ? null : visibleTabs.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No campaign categories available.
          </Typography>
        ) : (
          <Tabs
            value={activeTab ?? false}
            onChange={handleTabChange}
            aria-label="campaign type tabs"
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mb: 2 }}
          >
            {visibleTabs.map(renderTab)}
          </Tabs>
        )}

        {!loading && !showError && activeTab && (
          <Box data-testid="campaign-type-search-slot" sx={{ mb: 2, height: 40 }}>
            <TextField
              placeholder="Search campaign types..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" flex="1" sx={{ marginTop: 2 }}>
            <CircularProgress />
          </Box>
        ) : showError ? (
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            flex="1"
            gap={2}
            sx={{ minHeight: 200 }}
          >
            <Typography variant="body2" color="text.secondary">
              Failed to load campaign types. Please try again.
            </Typography>
            <Button variant="outlined" size="small" onClick={handleRetry} disabled={loading}>
              Retry
            </Button>
          </Box>
        ) : activeTab ? (
          <Box sx={{ position: 'relative' }}>
            {showScrollArrows && canScrollUp && (
              <RaisedIconButton
                onClick={scrollUp}
                sx={{
                  position: 'absolute',
                  top: -15,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 2,
                  marginBottom: 1,
                }}
                size="small"
              >
                <KeyboardArrowUpIcon fontSize="small" />
              </RaisedIconButton>
            )}
            <HiddenScrollbarArea
              ref={scrollContainerRef}
              onScroll={checkScrollPosition}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                overflowY: 'auto',
                maxHeight: 420,
                minHeight: 420,
                ...(visibleOptions.length === 0 && {
                  justifyContent: 'center',
                  alignItems: 'center',
                }),
                '& > *': { flexShrink: 0 },
              }}
            >
            {visibleOptions.length === 0 ? (
              isFavoritesEmpty ? (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  gap={1}
                  sx={{ textAlign: 'center', px: 3, mb: 12 }}
                >
                  <Box display="flex" fontSize={44}>
                    <FavoriteStarIcon favorite={false} />
                  </Box>
                  <Typography variant="subtitle1" color="text.primary">
                    No favorites yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Click the star next to a campaign to add it here for quick access.
                  </Typography>
                </Box>
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" flex="1" sx={{ minHeight: 200 }}>
                  <Typography variant="body2" color="text.secondary">
                    No campaign types match your search.
                  </Typography>
                </Box>
              )
            ) : (
              visibleOptions.map(option => {
                if (option.kind === 'preset' && option.preset) {
                  const sourceConfig = option.preset.sourceConfigId
                    ? configsById[option.preset.sourceConfigId]
                    : undefined;

                  return (
                    <PresetCard
                      key={`preset-${option.id}`}
                      preset={option.preset}
                      canArchive={canArchive}
                      canEdit={canEditPresets}
                      favorite={option.favorite}
                      favoritePending={pendingIds.has(`preset:${option.id}`)}
                      icon={
                        sourceConfig
                          ? getConfigIcon(sourceConfig.config.name, sourceConfig.config.icon)
                          : undefined
                      }
                      onUse={() => handleOptionClick(option)}
                      onArchive={() => handleArchivePreset(option)}
                      onEdit={preset => setEditingPreset(preset)}
                      onFavorite={() => toggleFavorite(option)}
                    />
                  );
                }

                if (option.kind === 'config' && option.config) {
                  const config = option.config;
                  const authorLabel = config.authorIsCurrentUser ? 'You' : config.authorName;
                  return (
                    <Card key={`config-${option.id}`} variant="outlined" sx={{ position: 'relative' }}>
                      <CardActionArea onClick={() => handleOptionClick(option)} sx={{ position: 'relative' }}>
                        <CardContent sx={{ p: 2 }}>
                          <Box display="flex" alignItems="center" gap={1.5}>
                            <Box>{getConfigIcon(config.config.name, config.config.icon)}</Box>
                            <Box sx={{ flex: 1, minWidth: 0, pr: 4 }}>
                              <Box display="flex" alignItems="baseline" gap={0.5} sx={{ mb: 0.5, minWidth: 0 }}>
                                <Typography variant="subtitle1" noWrap sx={{ minWidth: 0, lineHeight: 1.3 }}>
                                  {config.config.name}
                                </Typography>
                                {authorLabel && <AuthorLabel author={authorLabel} />}
                              </Box>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  lineHeight: 1.3,
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis'
                                }}
                              >
                                {config.config.description}
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </CardActionArea>
                      <IconButton
                        onClick={e => {
                          e.stopPropagation();
                          toggleFavorite(option);
                        }}
                        disabled={pendingIds.has(`config:${option.id}`)}
                        aria-label={option.favorite ? 'Remove from favorites' : 'Add to favorites'}
                        size="small"
                        sx={{ position: 'absolute', top: '50%', right: 8, transform: 'translateY(-50%)', zIndex: 1 }}
                      >
                        <FavoriteStarIcon favorite={option.favorite} tone="warning" fontSize="small" />
                      </IconButton>
                    </Card>
                  );
                }

                return null;
              })
            )}
            </HiddenScrollbarArea>
            {showScrollArrows && canScrollDown && (
              <RaisedIconButton
                onClick={scrollDown}
                sx={{
                  position: 'absolute',
                  bottom: -20,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 2,
                  marginTop: 1,
                }}
                size="small"
              >
                <KeyboardArrowDownIcon fontSize="small" />
              </RaisedIconButton>
            )}
          </Box>
        ) : null}
      </Box>
      {editingPreset && companyId && (
        <EditPresetDialog
          open
          companyId={companyId}
          preset={editingPreset}
          onClose={() => setEditingPreset(null)}
        />
      )}
      <ConfirmationDialog />
    </BhiDialog>
  );
};
